import { Badge } from '@/ui/Badge';
import { Skeleton } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { useGeneral } from './api';
import { Indicador, Indicadores, Titulo } from './piezas';

/**
 * General — el resumen del portal entero, por temas.
 *
 * El endpoint es tolerante a fallos por módulo: cada cifra va envuelta en su
 * propio `try`, y la que falle llega como 0. Por eso aquí no hay más estados
 * que «cargando», «falló entero» y los números.
 */
export function General({ activa }: { activa: boolean }) {
  const consulta = useGeneral(activa);
  const o = consulta.data;

  if (consulta.isPending) {
    return (
      <div className="flex flex-col gap-4" aria-busy="true">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }
  if (consulta.isError) {
    return (
      <EstadoError
        err={consulta.error}
        que="el resumen general"
        onReintentar={() => void consulta.refetch()}
      />
    );
  }
  if (!o) return null;

  const estados = Object.entries(o.solicitudes_por_estado ?? {});

  return (
    <div className="flex flex-col gap-6">
      <section aria-labelledby="gen-personas">
        <Titulo icono="bi-people-fill"><span id="gen-personas">Personas</span></Titulo>
        <Indicadores>
          <Indicador
            icono="bi-person-check-fill" valor={o.usuarios_activos} etiqueta="Usuarios activos"
            nota={`${o.usuarios_inactivos} inactivos · ${o.areas} áreas`}
          />
        </Indicadores>
      </section>

      <section aria-labelledby="gen-comunicacion">
        <Titulo icono="bi-megaphone-fill"><span id="gen-comunicacion">Comunicación</span></Titulo>
        <Indicadores>
          <Indicador
            icono="bi-chat-square-text-fill" valor={o.publicaciones} etiqueta="Publicaciones"
            nota={`${o.comentarios} comentarios · ${o.reacciones} reacciones`}
          />
          <Indicador
            icono="bi-flag-fill" estado={o.reportes_pendientes > 0 ? 'malo' : null}
            valor={o.reportes_pendientes} etiqueta="Reportes pendientes" nota="moderación"
          />
          <Indicador
            icono="bi-collection-fill" valor={o.comunidades} etiqueta="Comunidades"
            nota={`${o.miembros_comunidades} miembros`}
          />
        </Indicadores>
      </section>

      <section aria-labelledby="gen-formacion">
        <Titulo icono="bi-mortarboard-fill"><span id="gen-formacion">Formación</span></Titulo>
        <Indicadores>
          <Indicador
            icono="bi-journal-text" valor={o.cursos} etiqueta="Cursos activos"
            nota={`${o.inscripciones} inscripciones`}
          />
          <Indicador icono="bi-check-circle-fill" valor={o.cursos_completados} etiqueta="Cursos completados" />
          <Indicador icono="bi-award-fill" valor={o.certificados_vigentes} etiqueta="Certificados" nota="no revocados" />
        </Indicadores>
      </section>

      <section aria-labelledby="gen-eventos">
        <Titulo icono="bi-calendar-event-fill"><span id="gen-eventos">Eventos y encuestas</span></Titulo>
        <Indicadores>
          <Indicador
            icono="bi-calendar2-week-fill" valor={o.eventos_total} etiqueta="Eventos"
            nota={`${o.eventos_proximos} próximos`}
          />
          <Indicador icono="bi-person-lines-fill" valor={o.inscripciones_eventos} etiqueta="Inscripciones a eventos" />
          <Indicador
            icono="bi-clipboard-data-fill" valor={o.encuestas_activas} etiqueta="Encuestas activas"
            nota={`${o.respuestas_encuestas} respuestas`}
          />
        </Indicadores>
      </section>

      <section aria-labelledby="gen-solicitudes">
        <Titulo icono="bi-star-fill"><span id="gen-solicitudes">Reconocimientos y solicitudes</span></Titulo>
        <Indicadores>
          <Indicador icono="bi-star-fill" valor={o.kudos} etiqueta="Kudos entregados" />
          <Indicador icono="bi-file-earmark-text-fill" valor={o.solicitudes_total} etiqueta="Solicitudes de documentos" />
        </Indicadores>
        <p className="mt-3 flex flex-wrap items-center gap-2 rounded-sm bg-sunken p-3 text-3">
          <strong className="text-brand-ink">Solicitudes por estado:</strong>
          {estados.length ? (
            estados.map(([estado, cuantas]) => (
              <Badge key={estado}>{estado}: {cuantas}</Badge>
            ))
          ) : (
            <span className="text-muted">sin solicitudes</span>
          )}
        </p>
      </section>
    </div>
  );
}
